import { Injectable } from '@angular/core';

@Injectable()
export class LayoutScrollService {
  private contentElement: HTMLElement | null = null;

  public setContentElement(contentElement: HTMLElement | null): void {
    this.contentElement = contentElement;
  }

  public scrollContentTo(opts: ScrollToOptions): void {
    if (!this.contentElement?.scrollTo) {
      console.warn('Unable to scroll content because elem does not exist or method scrollTo is unavailable');
      return;
    }

    this.contentElement.scrollTo(opts);
  }

  public scrollElementIntoContent(
    targetElement: HTMLElement,
    block: ScrollLogicalPosition = 'center',
    behavior: ScrollBehavior = 'smooth'
  ): void {
    if (!this.contentElement?.scrollTo) {
      console.warn('Unable to scroll content because elem does not exist or method scrollTo is unavailable');
      return;
    }

    const contentRect = this.contentElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    const visibleBounds = this.getContentVisibleBounds(this.contentElement, contentRect);
    const targetTop = this.contentElement.scrollTop + targetRect.top - contentRect.top;
    const targetBottom = this.contentElement.scrollTop + targetRect.bottom - contentRect.top;
    const visibleTopOffset = visibleBounds.top - contentRect.top;
    const visibleHeight = visibleBounds.bottom - visibleBounds.top;
    let top: number;

    switch (block) {
      case 'center':
        top = targetTop - visibleTopOffset - (visibleHeight - targetRect.height) / 2;
        break;
      case 'end':
        top = targetBottom - visibleTopOffset - visibleHeight;
        break;
      case 'nearest': {
        const isFullyVisible = targetRect.top >= visibleBounds.top && targetRect.bottom <= visibleBounds.bottom;
        if (isFullyVisible) {
          return;
        }
        top =
          targetRect.top < visibleBounds.top
            ? targetTop - visibleTopOffset
            : targetBottom - visibleTopOffset - visibleHeight;
        break;
      }
      case 'start':
      default:
        top = targetTop - visibleTopOffset;
        break;
    }

    const maxScrollTop = Math.max(this.contentElement.scrollHeight - this.contentElement.clientHeight, 0);
    this.contentElement.scrollTo({ top: Math.min(Math.max(top, 0), maxScrollTop), behavior });
  }

  public destroy(): void {
    this.contentElement = null;
  }

  private getContentVisibleBounds(contentElement: HTMLElement, contentRect: DOMRect): { top: number; bottom: number } {
    const visualViewport = window.visualViewport;
    const viewportTop = visualViewport?.offsetTop || 0;
    const viewportBottom = viewportTop + (visualViewport?.height || window.innerHeight);
    const top = Math.max(contentRect.top, viewportTop);
    const bottom = Math.min(contentRect.bottom, viewportBottom);
    const bottomOverlayHeight = this.getBottomOverlayHeight(contentElement, contentRect, top, bottom);

    return {
      top,
      bottom: Math.max(top, bottom - bottomOverlayHeight),
    };
  }

  private getBottomOverlayHeight(
    contentElement: HTMLElement,
    contentRect: DOMRect,
    visibleTop: number,
    visibleBottom: number
  ): number {
    if (!contentElement.classList.contains('with-bottom-bar')) {
      return 0;
    }

    return Array.from(contentElement.querySelectorAll<HTMLElement>('*')).reduce((overlayHeight, element) => {
      const elementStyle = window.getComputedStyle(element);
      const canOverlayContent = ['fixed', 'absolute', 'sticky'].includes(elementStyle.position);

      if (!canOverlayContent || elementStyle.visibility === 'hidden' || elementStyle.display === 'none') {
        return overlayHeight;
      }

      const elementRect = element.getBoundingClientRect();
      const overlapsContentHorizontally = elementRect.left < contentRect.right && elementRect.right > contentRect.left;
      const coversMostOfContentWidth = elementRect.width >= contentRect.width * 0.5;
      const overlapsVisibleBottom = elementRect.top < visibleBottom && elementRect.bottom >= visibleBottom - 1;

      if (!overlapsContentHorizontally || !coversMostOfContentWidth || !overlapsVisibleBottom) {
        return overlayHeight;
      }

      return Math.max(overlayHeight, visibleBottom - Math.max(elementRect.top, visibleTop));
    }, 0);
  }
}
